import type { DomainGoalRecord } from '~/models/fitnessDomain'
import { isPlanned } from '~/models/fitnessDomain'
import type { NutritionPlanRecord } from '~/models/nutritionPlan'
import type { RoutineAnchorRecord } from '~/models/routineAnchor'
import { DB_SCHEMA_VERSION } from '~/models/schemaVersion'
import { standardWeekFor } from '~/models/trainingSchedule'
import type { Weekday } from '~/models/weekday'
import type { PlanSnapshot, PlanSnapshotGoal } from '~/models/weeklyReview'
import { RULES_ENGINE_VERSION } from '~/rules/version'
import { getActiveNutritionPlan } from '~/services/dailyCheckService'
import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import { listParticipatingDomainGoals } from '~/storage/repositories/domainGoalRepository'
import { getActiveProject } from '~/storage/repositories/projectRepository'
import { listRoutineAnchors } from '~/storage/repositories/routineAnchorRepository'
import { unwrapTime } from '~/types/dates'
import type { Result } from '~/utils/result'
import { ok } from '~/utils/result'
import { getEffectiveWeekStartDay } from './weekBoundaryService'

/**
 * Freezing the plan a week was judged against (D-099, as amended at C6.5).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  BUILT FROM THE LIVE PLAN, ONCE, AT FINALIZATION — AND NEVER AGAIN
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  There is deliberately **no function here that rebuilds a snapshot for a past
 *  week**, and none may be added. Reconstructing one from the current plan is
 *  exactly the retroactive re-judgement D-046 forbids: it would report what the
 *  user is doing *now* as though it were what they were asked to do *then*, and
 *  the difference is invisible in the output.
 *
 *  A review finalized before C6.5 therefore has no snapshot and never will. That
 *  is an honest gap, and every reader states it rather than filling it in.
 */

/**
 * Assemble a snapshot from records already read.
 *
 * Pure, so the shape can be tested against fixtures without a database — and so
 * the decision about *what* is frozen is separable from the six reads that fetch
 * it.
 */
export function buildPlanSnapshot(input: {
  readonly goals: readonly DomainGoalRecord[]
  readonly nutritionPlan: NutritionPlanRecord | undefined
  readonly anchors: readonly RoutineAnchorRecord[]
  /** Needed to lay a schedule out in week order; the caller reads it once. */
  readonly weekStartDay: Weekday
}): PlanSnapshot {
  const { weekStartDay } = input
  const goals: PlanSnapshotGoal[] = input.goals.map((goal) => ({
    domain: goal.domain,
    intention: goal.intention,
    priority: goal.priority,
    /* Only for a goal that is actually planned against. A `trackOnly` domain
       collects evidence and is never asked anything, so recording an empty
       commitment for it would read as a plan the user failed to meet. */
    ...(isPlanned(goal) && goal.weeklyCommitment !== undefined
      ? {
          /* Through the one reader, so a scheduled goal's standard week is the
             derivation rather than the superseded `[]` left on the record
             (D-105 §3). Reimplementing it here is how the two-authority shape
             comes back without anyone deciding to. */
          standard: standardWeekFor(goal, weekStartDay),
          minimumViable: goal.weeklyCommitment.minimumViable,
        }
      : {}),
    /* Frozen alongside the derived week. Only for a goal that is planned
       against, for the same reason as the commitment above. */
    ...(isPlanned(goal) && goal.weeklySchedule !== undefined && goal.weeklySchedule.length > 0
      ? { schedule: goal.weeklySchedule }
      : {}),
  }))

  const plan = input.nutritionPlan

  return {
    activeDomains: input.goals.map((goal) => goal.domain),
    goals,
    ...(plan === undefined
      ? {}
      : {
          nutrition: {
            source: plan.source,
            ...(plan.providerLabel === undefined ? {} : { providerLabel: plan.providerLabel }),
            ...(plan.calorieTarget === undefined ? {} : { calorieTarget: plan.calorieTarget }),
            ...(plan.calorieTargetBasis === undefined
              ? {}
              : { calorieTargetBasis: plan.calorieTargetBasis }),
            ...(plan.proteinTargetG === undefined ? {} : { proteinTargetG: plan.proteinTargetG }),
          },
        }),
    /* Enabled anchors only. A kind the user switched off was not part of that
       week's routine, and recording it would imply an intention they had
       withdrawn. */
    anchors: input.anchors
      .filter((anchor) => anchor.enabled === 'yes' && anchor.startTime !== undefined)
      .map((anchor) => ({
        kind: anchor.kind,
        ...(anchor.startTime === undefined ? {} : { startTime: unwrapTime(anchor.startTime) }),
      })),
    rulesVersion: RULES_ENGINE_VERSION,
    schemaVersion: DB_SCHEMA_VERSION,
  }
}

/**
 * Read the live plan and freeze it.
 *
 * Returns `undefined` rather than an empty snapshot when there is no project:
 * **absent means "not recorded", never "an empty plan"**, and the two render
 * differently and mean opposite things.
 */
export async function collectPlanSnapshot(
  db: SolaDatabase,
  now: Date,
): Promise<Result<PlanSnapshot | undefined, StorageError>> {
  const project = await getActiveProject(db)
  if (!project.ok) return project
  if (project.value === undefined) return ok(undefined)

  const [goals, nutritionPlan, anchors, weekStartDay] = await Promise.all([
    listParticipatingDomainGoals(db, project.value.id),
    getActiveNutritionPlan(db),
    listRoutineAnchors(db),
    getEffectiveWeekStartDay(db, now),
  ])
  if (!goals.ok) return goals
  if (!nutritionPlan.ok) return nutritionPlan
  if (!anchors.ok) return anchors
  if (!weekStartDay.ok) return weekStartDay

  return ok(
    buildPlanSnapshot({
      goals: goals.value,
      nutritionPlan: nutritionPlan.value,
      anchors: anchors.value,
      weekStartDay: weekStartDay.value,
    }),
  )
}
